"use client";

import React, { useEffect, useRef, useState } from "react";
import { Box, Spinner, Heading, Flex, Tooltip } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import axios from "axios";
import echarts from "./lib";

// Semantic ABC colours (same Chakra hues the old chart used — the header
// info text names them): A = green.500, B = orange.400, C = red.500.
const SEGMENT_COLORS = {
  A: "#38A169",
  B: "#ED8936",
  C: "#E53E3E",
};

const fmtShort = (value) => {
  if (value >= 1000000) return `R${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R${(value / 1000).toFixed(0)}K`;
  return `R${value.toLocaleString("en-US")}`;
};

const SEGMENT_DESC = {
  A: "top 20% of revenue",
  B: "next 30%",
  C: "remaining 50%",
};

const barGradient = (base) => ({
  type: "linear",
  x: 0,
  y: 0,
  x2: 1,
  y2: 0,
  colorStops: [
    { offset: 0, color: base + "66" }, // ~40% alpha at the base
    { offset: 1, color: base },
  ],
});

const Chart10 = ({ supplier, retailer }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let disposed = false;

    const build = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/chart10`,
          { supplier, retailer, userid: localStorage.getItem("userid") }
        );

        // Identical to the AmCharts version — rows { sku, value, segment }
        // used raw, in API order, with the same rows*40px dynamic height.
        const data = res.data.data;

        if (disposed || !containerRef.current) return;

        const height = Math.max(data.length * 40, 300);
        containerRef.current.style.height = `${height}px`;

        const chart = echarts.init(containerRef.current);
        chartRef.current = chart;
        chart.setOption({
          grid: { left: 8, right: 56, top: 26, bottom: 4, containLabel: true },
          legend: {
            top: 0,
            left: 0,
            itemWidth: 10,
            itemHeight: 10,
            icon: "roundRect",
            itemGap: 14,
            textStyle: { color: "#4A5D6B", fontSize: 11.5 },
            data: [
              { name: "A — top 20%", itemStyle: { color: SEGMENT_COLORS.A } },
              { name: "B — next 30%", itemStyle: { color: SEGMENT_COLORS.B } },
              { name: "C — remaining 50%", itemStyle: { color: SEGMENT_COLORS.C } },
            ],
            selectedMode: false,
          },
          xAxis: {
            type: "value",
            axisLabel: { color: "#64798A", fontSize: 10.5, formatter: fmtShort },
            splitLine: { lineStyle: { color: "#E2E9EE", type: [3, 3] } },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          yAxis: {
            type: "category",
            inverse: true,
            data: data.map((d) => d.sku),
            axisLabel: {
              color: "#64798A",
              fontSize: 10.5,
              fontFamily: "Inter, sans-serif",
              width: 190,
              overflow: "truncate",
            },
            axisLine: { show: false },
            axisTick: { show: false },
            triggerEvent: true,
          },
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow", shadowStyle: { color: "rgba(29,47,58,0.05)" } },
            confine: true,
            backgroundColor: "#ffffff",
            borderColor: "#E2E9EE",
            borderWidth: 1,
            padding: [8, 12],
            textStyle: { color: "#1D2F3A", fontSize: 12 },
            extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
            formatter: (params) => {
              const p = params[0];
              const row = data[p.dataIndex];
              const seg = row?.segment;
              const segColor = SEGMENT_COLORS[seg] || "#64798A";
              return `${row?.sku}<br/><b>R${Number(p.value).toLocaleString("en-US")}</b> last 12 months<br/><span style="color:${segColor};font-weight:600">Segment ${seg}</span> — ${
                SEGMENT_DESC[seg] || ""
              }`;
            },
          },
          series: [
            // Three legend-only stubs so the A/B/C chips render with the right
            // colours. Line type with no data — an empty BAR series would still
            // claim a cluster slot and offset the real bars.
            { name: "A — top 20%", type: "line", data: [], itemStyle: { color: SEGMENT_COLORS.A } },
            { name: "B — next 30%", type: "line", data: [], itemStyle: { color: SEGMENT_COLORS.B } },
            { name: "C — remaining 50%", type: "line", data: [], itemStyle: { color: SEGMENT_COLORS.C } },
            {
              type: "bar",
              barWidth: "45%",
              showBackground: true,
              backgroundStyle: { color: "#F6F8FA", borderRadius: [0, 5, 5, 0] },
              data: data.map((d) => ({
                value: d.value,
                itemStyle: {
                  borderRadius: [0, 5, 5, 0],
                  color: barGradient(SEGMENT_COLORS[d.segment] || "#64798A"),
                },
              })),
              label: {
                show: true,
                position: "right",
                distance: 6,
                formatter: (p) => fmtShort(p.value),
                color: "#4A5D6B",
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: "Inter, sans-serif",
              },
              labelLayout: { hideOverlap: true },
              emphasis: {
                itemStyle: { shadowBlur: 10, shadowColor: "rgba(29,47,58,0.22)" },
              },
              animationDuration: 900,
              animationDelay: (idx) => idx * 25,
              animationEasing: "cubicOut",
            },
          ],
        });
      } catch (err) {
        console.error("Chart10 fetch error", err);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    build();

    const ro = new ResizeObserver(() => {
      if (chartRef.current) chartRef.current.resize();
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      disposed = true;
      ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [supplier, retailer]);

  return (
    <Box w="100%" h="100%" position="relative">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="xs" color="gray.600" fontWeight="600">
          ABC SKU Segmentation – Last 12 Months
        </Heading>
        <Tooltip
          label="Last-12-month revenue by product. Green (A)=top 20%, Orange (B)=next 30%, Red (C)=remaining 50%."
          hasArrow
          placement="left"
          maxW="240px"
          whiteSpace="normal"
        >
          <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
        </Tooltip>
      </Flex>
      <div
        ref={containerRef}
        style={{ width: "100%", height: "calc(100% - 40px)" }}
      />
      {loading && (
        <Spinner
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
        />
      )}
    </Box>
  );
};

export default Chart10;
